import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { merge } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import {
  Interaction,
  InteractionFilterMode,
  InteractionStatus,
} from '../../models/interactions.model';
import { SecondsToDurationPipe } from '../../pipes/seconds-to-duration.pipe';
import { InteractionService } from '../../services/interaction.service';
import { PermissionService } from '@ipg/ngx-frontend-toolset';
import { TagResponse, TagService } from '../../services/tag.service';
import { BaseDialog } from '../dialog/base-dialog';
import { ConfirmHandler } from '../dialog/dialog.model';
import { TagFilterComponent } from '../tag-filter/tag-filter.component';
import { InteractionSelectionDialogData } from './services/interaction-selection-dialog.service';

@Component({
  selector: 'app-interaction-selection-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatListOption,
    MatSelectionList,
    MatTooltip,
    MatProgressSpinner,
    SecondsToDurationPipe,
    MatFormField,
    MatInput,
    MatIcon,
    MatIconButton,
    MatPrefix,
    MatSuffix,
    FormsModule,
    TagFilterComponent,
  ],
  templateUrl: './interaction-selection-dialog.component.html',
  styleUrl: './interaction-selection-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractionSelectionDialogComponent extends BaseDialog<
  InteractionSelectionDialogData & {
    onConfirm?: ConfirmHandler;
  }
> {
  private readonly _dialogRef =
    inject<MatDialogRef<InteractionSelectionDialogComponent>>(MatDialogRef);

  private readonly _location = inject(Location);
  private readonly _interactions = inject(InteractionService);
  private readonly _tagService = inject(TagService);
  private readonly _permissionService = inject(PermissionService);

  private readonly _keywordInput$ = new Subject<string | null>();
  private readonly _debouncedKeyword$ = this._keywordInput$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap(() => this._query.update((currentState) => ({ ...currentState, page: 1 })))
  );

  protected readonly keyword = signal<string | null>(this._restoreKeyword());
  protected readonly restoredSearch = signal(this._restoreKeyword() != null);
  protected readonly restoredTags = signal(this._restoreTagIds().length > 0);
  protected readonly selectedTagIds = signal<string[]>(this._restoreTagIds());
  protected readonly filterTags = signal<TagResponse[]>([]);

  /** See `ClientInteractionsComponent.canReadTags` -- same filter, same gate. */
  protected readonly canReadTags = computed(() =>
    this._permissionService.hasPermission('tag:read')
  );
  protected readonly selectedTags = computed(() =>
    this.filterTags()
      .filter((t) => this.selectedTagIds().includes(t.id))
      .sort((a, b) => a.label.localeCompare(b.label))
  );

  private readonly _query = signal({
    page: 1,
    limit: 10,
  });

  private readonly _sort = signal({
    sortBy: 'created_at',
    sortOrder: 'desc',
  });

  protected readonly selectedIds = signal<Set<string>>(new Set());

  protected readonly creating = signal(false);

  protected readonly createInteractionHref = this._location.prepareExternalUrl(
    this.data.clientId
      ? `clients/${this.data.clientId}/interactions/new`
      : 'templates/new'
  );

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    keyword: this._debouncedKeyword$.pipe(startWith(this.keyword())),
    tagIds: toObservable(this.selectedTagIds).pipe(
      tap((ids) => this._persistTagIds(ids))
    ),
  }).pipe(debounceTime(0));

  private readonly _paginatedInteractions$ = this._params$.pipe(
    switchMap(({ query, sort, keyword, tagIds }) =>
      this._interactions.getAll({
        params: {
          ...(this.clientId
            ? {
                clientId: this.clientId,
              }
            : null),
          ...(this.status ? { status: this.status } : null),
          ...(this.filterMode ? { mode: this.filterMode } : null),
          ...(this.includeShared ? { includeShared: true } : null),
          ...(keyword ? { q: keyword } : null),
          ...(tagIds.length ? { tagIds } : null),
          ...query,
          ...sort,
        },
      })
    ),
    shareReplay(1)
  );

  private readonly _meta$ = this._paginatedInteractions$.pipe(map(({ meta }) => meta));

  private readonly _paginatedMetadata = toSignal(this._meta$);

  private readonly _accumulatedInteractions$ = this._paginatedInteractions$.pipe(
    scan((acc, curr) => {
      if (curr.meta.page === 1) {
        this.selectedIds.set(new Set());
        return curr.items;
      }

      return [...acc, ...curr.items];
    }, [] as Interaction[]),
    map((result) => {
      return result.filter((value) =>
        typeof this.data.exclude === 'function' ? !this.data.exclude(value) : true
      );
    })
  );

  private readonly _state = signal<InteractionSelectionDialogData>({});

  protected readonly dataSource = toSignal(this._accumulatedInteractions$, {
    initialValue: [],
  });

  protected readonly isLastPage = computed(
    () => this._paginatedMetadata()?.lastPage ?? false
  );

  protected readonly showLoadMore = computed(
    () => this.dataSource().length > 0 && !this.isLastPage()
  );



  protected readonly fetching = signal(false);
  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  protected readonly maxSelectionLimit = computed(() => {
    const { maxSelectionLimit, initialSelectionCount = 0 } = this._state();

    return maxSelectionLimit
      ? Math.max(0, maxSelectionLimit - initialSelectionCount)
      : undefined;
  });

  get multiple() {
    return this._state().multiple ?? true;
  }

  get clientId() {
    return this._state().clientId;
  }

  get status() {
    return this._state().status;
  }

  get filterMode() {
    return this._state().mode;
  }

  get includeShared() {
    return this._state().includeShared ?? false;
  }

  get allowCreate() {
    return this._state().allowCreate ?? false;
  }

  get storageKey() {
    return this._state().storageKey;
  }

  get selectedItems(): Interaction[] {
    const ids = this.selectedIds();
    return this.dataSource().filter((item) => ids.has(item._id));
  }

  constructor() {
    super();

    this._setState(this.data);

    const clientId = this.clientId ?? undefined;
    const mode = this.filterMode;
    const status = this.status;

    // Pass clientId, mode and status so the backend applies the right scope:
    // - clientId + mode='schedule' → client interactions AND templates
    // - clientId only            → client interactions only
    // - mode='none' only         → templates only
    // - status                   → restrict to specific statuses (e.g. published)
    const includeShared = this.includeShared;

    const params:
      | {
          clientId?: string;
          mode?: InteractionFilterMode;
          status?: InteractionStatus[];
          includeShared?: boolean;
        }
      | undefined =
      clientId || mode || status?.length || includeShared
        ? {
            ...(clientId ? { clientId } : {}),
            ...(mode ? { mode } : {}),
            ...(status?.length ? { status } : {}),
            ...(includeShared ? { includeShared: true } : {}),
          }
        : undefined;

    if (this.canReadTags()) {
      this._tagService
        .getUsedTags(params)
        .subscribe((tags) => this.filterTags.set(tags));
    }
  }

  isItemDisabled(item: Interaction) {
    const isSelected = this.selectedIds().has(item._id);
    const maxSelectionLimit = this.maxSelectionLimit();

    const limitReached =
      maxSelectionLimit != null && this.selectedIds().size >= maxSelectionLimit;

    return limitReached && !isSelected;
  }

  handleSelectionChange(event: MatSelectionListChange): void {
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      for (const option of event.options) {
        if (option.selected) {
          next.add(option.value._id);
        } else {
          next.delete(option.value._id);
        }
      }
      return next;
    });
  }

  loadMoreItems() {
    if (this.isLastPage() || this.fetching()) {
      return;
    }

    this._query.update((currentState) => ({
      ...currentState,
      page: currentState.page + 1,
    }));
  }

  handleSearch(value: string) {
    const trimmed = value.trim();
    this.keyword.set(trimmed);
    this._keywordInput$.next(trimmed);
    this._persistKeyword(trimmed);
  }

  clearSearch() {
    this.keyword.set(null);
    this._keywordInput$.next(null);
    this._persistKeyword(null);
  }

  handleTagFilterChange(ids: string[]): void {
    this.selectedTagIds.set(ids);
    this._query.update((state) => ({ ...state, page: 1 }));
  }

  removeTag(id: string): void {
    this.selectedTagIds.update((current) => current.filter((i) => i !== id));
    this._query.update((state) => ({ ...state, page: 1 }));
  }

  protected handleCreateInteraction(event: Event): void {
    event.preventDefault();
    if (this.creating()) return;

    this.creating.set(true);

    const path = this.data.clientId
      ? `clients/${this.data.clientId}/interactions/new`
      : 'templates/new';
    window.location.href = this._location.prepareExternalUrl(path);
  }

  protected openFlowView(event: Event, item: Interaction): void {
    event.stopPropagation();
    const path = item.clientId === null ? 'templates' : 'interactions';
    window.open(
      this._location.prepareExternalUrl(`${path}/${item._id}/flow`),
      '_blank'
    );
  }

  override handleConfirm(): void {
    this.submitted.set(true);

    const selection = this.selectedItems;

    if (this.data.onConfirm) {
      const callback$ = this.data.onConfirm(selection);

      const source$ = callback$.pipe(
        shareReplay(1),
        catchError((error) => {
          this.submitted.set(false);

          return throwError(() => error);
        })
      );

      const loading$ = merge(
        timer(1000).pipe(
          map(() => true),
          takeUntil(source$)
        ),

        source$.pipe(map(() => false))
      );

      loading$
        .pipe(
          tap((state) => this.loading.set(state)),
          filter((state) => !state),
          tap(() => this._dialogRef.close(true)),
          take(1)
        )
        .subscribe();

      return;
    }

    this._dialogRef.close(true);
  }

  private _setState(newState: Partial<InteractionSelectionDialogData>) {
    this._state.update((currentState) => ({
      ...currentState,
      ...newState,
    }));
  }

  private _restoreKeyword(): string | null {
    const key = this.data.storageKey;
    if (!key) return null;
    try {
      const stored = sessionStorage.getItem(`${key}:keyword`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private _restoreTagIds(): string[] {
    const key = this.data.storageKey;
    if (!key) return [];
    try {
      const stored = sessionStorage.getItem(`${key}:tagIds`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private _persistKeyword(value: string | null): void {
    const key = this.storageKey;
    if (!key) return;
    try {
      if (value) {
        sessionStorage.setItem(`${key}:keyword`, JSON.stringify(value));
      } else {
        sessionStorage.removeItem(`${key}:keyword`);
      }
    } catch { /* sessionStorage unavailable */ }
  }

  private _persistTagIds(ids: string[]): void {
    const key = this.storageKey;
    if (!key) return;
    try {
      if (ids.length) {
        sessionStorage.setItem(`${key}:tagIds`, JSON.stringify(ids));
      } else {
        sessionStorage.removeItem(`${key}:tagIds`);
      }
    } catch { /* sessionStorage unavailable */ }
  }
}
